import React from 'react';
import { DrawerTrigger, Drawer, DrawerCloseButton, Heading, Text, Flex, Box, Button, SplitButton, LoadingOrbit, Tabs, TabList, Tab, TabPanel, BrowserStreamPanel, TagBadge, type BadgeVariant, badgeVariantToTagColor } from '@chatsdet/ui';
import { ArrowSquareOut } from '@phosphor-icons/react';
import { useBrowserStream } from '../../../hooks/useBrowserStream';
import { getQaStatusMeta } from '../qaStatus';
import { API_URL } from '../../../config';
import { canContinueQaReport } from '../page-detail/pageDetailUi';
import type { QaReportSummary, QaReportTodoStatus } from '../page-detail/types';
import type { IssueSummary } from '@chatsdet/types/issues';
import { useIssuesBySource } from '../../../hooks/useIssuesBySource';
import {
  issueSeverityBadgeVariants,
  formatSeverityLabel,
} from '../../components/issues/issueBadges';
import {
  resolveIssuePageUrl,
  getIssuePageDisplayUrl,
} from '../../components/issues/issueUrls';
import { useAuth } from '../../../contexts/AuthContext';

type QaReportElementSummary = {
  id: string;
  label?: string | null;
  type?: string | null;
};

const ELEMENT_TYPE_LABELS: Record<string, string> = {
  data_table: 'Data table',
  form: 'Form',
  table: 'Table',
  button: 'Button',
  link: 'Link',
  input: 'Input',
  other: 'Other',
};

type ParsedTaskItem = {
  elementLabel: string | null;
  testName: string;
};

const parseTaskItem = (item: string): ParsedTaskItem => {
  const parts = item
    .split(' - ')
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length >= 2) {
    return {
      elementLabel: parts[0] || null,
      testName: parts.slice(1).join(' - ') || item,
    };
  }

  return { elementLabel: null, testName: item };
};

type QaReportDrawerProps = {
  report: QaReportSummary | null;
  isOpen: boolean;
  onClose: () => void;
  onRunAgent: () => void;
  onRunAgentWithLatestRun: () => void;
  onContinueRun: (reportId: string) => void;
  isRunning: boolean;
  isAttaching: boolean;
  isContinuing: boolean;
  formatDate: (value: string | null | undefined) => string;
  elements?: QaReportElementSummary[];
};

export function QaReportDrawer({
  report,
  isOpen,
  onClose,
  onRunAgent,
  onRunAgentWithLatestRun,
  onContinueRun,
  isRunning,
  isAttaching,
  isContinuing,
  formatDate,
  elements,
}: QaReportDrawerProps) {
  return (
    <DrawerTrigger isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
      <button style={{ display: 'none' }} aria-hidden />
      <Drawer hideCloseButton>
        {report ? (
          <QaReportDrawerContent
            report={report}
            onRunAgent={onRunAgent}
            onRunAgentWithLatestRun={onRunAgentWithLatestRun}
            onContinueRun={onContinueRun}
            isRunning={isRunning}
            isAttaching={isAttaching}
            isContinuing={isContinuing}
            onClose={onClose}
            formatDate={formatDate}
            isOpen={isOpen}
            elements={elements}
          />
        ) : (
          <div className="p-8 text-center space-y-3">
            <Heading size="sm">No QA report available</Heading>
            <Text size="sm" className="text-muted-foreground">
              Run the QA agent to generate a report.
            </Text>
          </div>
        )}
      </Drawer>
    </DrawerTrigger>
  );
}

type QaReportDrawerContentProps = {
  report: QaReportSummary;
  onRunAgent: () => void;
  onRunAgentWithLatestRun: () => void;
  onContinueRun: (reportId: string) => void;
  isRunning: boolean;
  isAttaching: boolean;
  isContinuing: boolean;
  onClose: () => void;
  formatDate: (value: string | null | undefined) => string;
  isOpen: boolean;
  elements?: QaReportElementSummary[];
};

function QaReportDrawerContent({
  report,
  onRunAgent,
  onRunAgentWithLatestRun,
  onContinueRun,
  isRunning,
  isAttaching,
  isContinuing,
  onClose,
  formatDate,
  isOpen,
  elements,
}: QaReportDrawerContentProps) {
  const { user } = useAuth();
  const isSuperadmin = user?.isSuperadmin === true;
  const [selectedTab, setSelectedTab] = React.useState<'overview' | 'observability'>('overview');
  const statusMeta = getQaStatusMeta(report.status);
  const tasks = report.todoItems ?? [];
  const hasTemplateStatuses = tasks.some((todo) =>
    ['healthy', 'unhealthy', 'not_applicable'].includes(todo.status)
  );
  const isTemplateTesting = hasTemplateStatuses;
  const actionEntries = report.actionsLog ?? [];
  const isLiveReport = report.status === 'running' || report.status === 'pending';
  const relatedIssuesQuery = useIssuesBySource(
    { sourceKind: 'qa_page_test', sourceId: report.id },
    { enabled: isOpen }
  );
  const relatedIssues = relatedIssuesQuery.data ?? [];
  const relatedIssuesError = relatedIssuesQuery.isError
    ? relatedIssuesQuery.error instanceof Error
      ? relatedIssuesQuery.error.message
      : 'Failed to load linked issues'
    : null;
  const canViewTrace = Boolean(report.traceStorageKey);
  const handleViewTrace = React.useCallback(() => {
    if (!canViewTrace) return;
    // window.open can't send the org header — carry it as a query param.
    const traceUrl = `${API_URL}/qa/reports/${report.id}/trace`;
    window.open(traceUrl, '_blank', 'noopener');
  }, [canViewTrace, report.id]);
  const failureMessage =
    report.status === 'failed'
      ? (report.summary ??
        (report.details &&
        typeof report.details === 'object' &&
        typeof (report.details as Record<string, unknown>).errorMessage === 'string'
          ? ((report.details as Record<string, unknown>).errorMessage as string)
          : null))
      : null;
  const failureDetailEntries =
    report.status === 'failed' && report.details && typeof report.details === 'object'
      ? (Object.entries(report.details as Record<string, unknown>)
          .filter(([, value]) => typeof value === 'string' || typeof value === 'number')
          .map(([key, value]) => [key, value as string | number]) as Array<
          [string, string | number]
        >)
      : [];
  const sentryBaseUrl =
    'https://tstc-9n.sentry.io/explore/logs/?logsFields=timestamp&logsFields=message&logsSortBys=-timestamp';
  const sentryQueryParts = [
    `qaReportId:${report.id}`,
    report.jobId ? `jobId:${report.jobId}` : null,
  ].filter(Boolean) as string[];
  const sentryUrl = `${sentryBaseUrl}&logsQuery=${encodeURIComponent(
    sentryQueryParts.join(' OR ')
  )}`;

  React.useEffect(() => {
    setSelectedTab('overview');
  }, [isOpen, isSuperadmin]);

  const canContinue = canContinueQaReport(report);
  const isBusy = isRunning || isAttaching || isContinuing;
  const primaryLabel = isRunning
    ? 'Starting…'
    : isAttaching
      ? 'Adding…'
      : isContinuing
        ? 'Continuing…'
        : canContinue
          ? 'Continue QA run'
          : 'Re-run QA agent';

  return (
    <div className="flex flex-col h-full">
      <header className="px-6 py-4 border-b space-y-3">
        <Flex align="start" justify="between" gap="sm" wrap>
          <div className="space-y-1 min-w-0">
            <Heading size="md">QA report</Heading>
            <Text size="sm" className="text-muted-foreground">
              {report.completedAt ? 'Completed' : 'Started'}{' '}
              {formatDate(report.completedAt ?? report.startedAt)}
            </Text>
          </div>
          <Flex align="center" gap="sm">
            <TagBadge name={statusMeta.label} color={badgeVariantToTagColor(statusMeta.badgeVariant)} hideDot />
            <DrawerCloseButton />
          </Flex>
        </Flex>
        <Text size="sm" className="text-muted-foreground">
          {report.summary ?? 'QA run completed without summary.'}
        </Text>
        <Flex align="center" gap="sm" wrap className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">
            Tests covered: {report.testedItems.length > 0 ? report.testedItems.length : 'None'}
          </span>
          <span aria-hidden>·</span>
          <span className="font-medium text-foreground">
            Issues found: {report.issuesFoundCount > 0 ? report.issuesFoundCount : 'None'}
          </span>
          <span aria-hidden>·</span>
          <span>Started {formatDate(report.startedAt ?? null)}</span>
        </Flex>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-6">
        {isSuperadmin ? (
          <Tabs
            selectedKey={selectedTab}
            onSelectionChange={(key) => setSelectedTab(String(key) as 'overview' | 'observability')}
            className="flex flex-col h-full"
          >
            <div className="mb-4">
              <TabList>
                <Tab id="overview">Overview</Tab>
                <Tab id="observability">Observability</Tab>
              </TabList>
            </div>
            <TabPanel id="overview" className="flex-1 overflow-auto p-0">
              <OverviewContent
                statusMeta={statusMeta}
                report={report}
                tasks={tasks}
                actionEntries={actionEntries}
                isLiveReport={isLiveReport}
                relatedIssuesQuery={relatedIssuesQuery}
                relatedIssues={relatedIssues}
                relatedIssuesError={relatedIssuesError}
                formatDate={formatDate}
                failureMessage={failureMessage}
                failureDetailEntries={failureDetailEntries}
                isTemplateTesting={isTemplateTesting}
                elements={elements}
              />
            </TabPanel>
            <TabPanel id="observability" className="flex-1 overflow-auto p-0">
              <ObservabilityContent
                canViewTrace={canViewTrace}
                onViewTrace={handleViewTrace}
                sentryUrl={sentryUrl}
                report={report}
              />
            </TabPanel>
          </Tabs>
        ) : (
          <OverviewContent
            statusMeta={statusMeta}
            report={report}
            tasks={tasks}
            actionEntries={actionEntries}
            isLiveReport={isLiveReport}
            relatedIssuesQuery={relatedIssuesQuery}
            relatedIssues={relatedIssues}
            relatedIssuesError={relatedIssuesError}
            formatDate={formatDate}
            failureMessage={failureMessage}
            failureDetailEntries={failureDetailEntries}
            isTemplateTesting={isTemplateTesting}
          />
        )}
      </div>

      <footer className="px-6 py-4 border-t bg-muted/30">
        <Flex gap="sm" wrap align="center" justify="end">
          <SplitButton
            variant="secondary"
            size="md"
            onPress={canContinue ? () => onContinueRun(report.id) : onRunAgent}
            isDisabled={isBusy}
          >
            {primaryLabel}
            {canContinue ? (
              <SplitButton.Item onAction={() => onContinueRun(report.id)} isDisabled={isBusy}>
                {isContinuing ? 'Continuing…' : 'Continue QA run'}
              </SplitButton.Item>
            ) : null}
            <SplitButton.Item onAction={() => onRunAgentWithLatestRun()} isDisabled={isBusy}>
              {isAttaching ? 'Adding to latest run…' : 'Run & add to latest QA run'}
            </SplitButton.Item>
          </SplitButton>
          <Button variant="ghost" onPress={onClose}>
            Close
          </Button>
        </Flex>
      </footer>
    </div>
  );
}

type OverviewContentProps = {
  statusMeta: ReturnType<typeof getQaStatusMeta>;
  report: QaReportSummary;
  tasks: NonNullable<QaReportSummary['todoItems']>;
  actionEntries: NonNullable<QaReportSummary['actionsLog']>;
  isLiveReport: boolean;
  relatedIssuesQuery: ReturnType<typeof useIssuesBySource>;
  relatedIssues: IssueSummary[];
  relatedIssuesError: string | null;
  formatDate: (value: string | null | undefined) => string;
  failureMessage: string | null;
  failureDetailEntries: Array<[string, string | number]>;
  isTemplateTesting: boolean;
  elements?: QaReportElementSummary[];
};

function OverviewContent({
  statusMeta: _statusMeta,
  report,
  tasks,
  actionEntries,
  isLiveReport,
  relatedIssuesQuery,
  relatedIssues,
  relatedIssuesError,
  formatDate: _formatDate,
  failureMessage,
  failureDetailEntries,
  isTemplateTesting,
  elements,
}: OverviewContentProps) {
  // Subscribe to browser stream when report is live
  const { state: browserStreamState } = useBrowserStream(isLiveReport ? report.id : null);

  const elementsById = React.useMemo(() => {
    const map = new Map<string, QaReportElementSummary>();
    (elements ?? []).forEach((el) => {
      if (el.id) {
        map.set(el.id, el);
      }
    });
    return map;
  }, [elements]);

  const taskCounts = React.useMemo(() => {
    const totals = {
      pending: 0,
      passed: 0,
      failed: 0,
      notApplicable: 0,
    };

    for (const task of tasks) {
      if (task.status === 'pending') {
        totals.pending += 1;
      } else if (task.status === 'unhealthy') {
        totals.failed += 1;
      } else if (task.status === 'not_applicable') {
        totals.notApplicable += 1;
      } else {
        totals.passed += 1;
      }
    }

    return totals;
  }, [tasks]);

  return (
    <div className="space-y-6">
      {failureMessage ? (
        <Box className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 space-y-2">
          <Text weight="medium" className="text-destructive">
            QA run failed
          </Text>
          <Text size="sm" className="text-destructive">
            {failureMessage}
          </Text>
          {failureDetailEntries.length > 0 ? (
            <div className="space-y-1">
              {failureDetailEntries.map(([key, value]) => (
                <Text key={key} size="xs" className="text-destructive/80">
                  {key}: {String(value)}
                </Text>
              ))}
            </div>
          ) : null}
        </Box>
      ) : null}

      <section className="space-y-2">
        <Flex align="center" justify="between" gap="sm">
          <Heading size="xs">Test tasks</Heading>
          {tasks.length > 0 ? (
            <Flex gap="xs" wrap>
              <TaskCountPill label="Passed" tone="success" count={taskCounts.passed} />
              <TaskCountPill label="Failed" tone="danger" count={taskCounts.failed} />
              <TaskCountPill label="Pending" tone="muted" count={taskCounts.pending} />
              <TaskCountPill label="N/A" tone="muted" count={taskCounts.notApplicable} />
            </Flex>
          ) : null}
        </Flex>
        {tasks.length > 0 ? (
          <div className="space-y-3">
            <div className="space-y-2">
              {tasks.map((task, index) => {
                const statusMeta = getTaskStatusMeta(task.status);
                const linkedElement =
                  (task.elementId ? elementsById.get(task.elementId) : null) ?? null;
                const parsed = parseTaskItem(task.item);
                const elementLabel =
                  linkedElement?.label ?? task.elementLabel ?? parsed.elementLabel;
                const elementType = linkedElement?.type ?? task.elementType ?? null;
                const typeLabel = elementType
                  ? (ELEMENT_TYPE_LABELS[elementType] ?? elementType)
                  : null;

                return (
                  <div
                    key={`${task.item}-${index}`}
                    className="rounded-lg border border-border/60 p-3 flex items-start gap-3"
                  >
                    <TagBadge name={statusMeta.label} color={badgeVariantToTagColor(statusMeta.variant)} hideDot size="sm" />
                    <div className="space-y-1 flex-1 min-w-0">
                      <Text className="text-sm font-medium">{parsed.testName}</Text>
                      {task.summary ? (
                        <Text size="xs" className="text-muted-foreground italic">
                          {task.summary}
                        </Text>
                      ) : null}
                      {elementLabel || typeLabel ? (
                        <Text size="xs" className="text-muted-foreground">
                          {elementLabel ?? 'Element'}
                          {typeLabel ? ` · ${typeLabel}` : ''}
                        </Text>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <Text size="sm" className="text-muted-foreground">
            No tasks recorded for this run.
          </Text>
        )}
      </section>

      {isLiveReport ? (
        <Box className="space-y-3">
          <BrowserStreamPanel
            state={browserStreamState}
            layout="stacked"
            showReasoningPanel={true}
            showActionHistory={true}
            emptyScreenshotLabel="Waiting for QA agent..."
            emptyReasoningLabel="Agent is thinking..."
            allowExpand={true}
          />
        </Box>
      ) : null}

      {!isTemplateTesting && actionEntries.length > 0 ? (
        <section className="space-y-2">
          <Heading size="xs">Action log</Heading>
          <div className="space-y-3">
            {actionEntries.map((entry, index) => (
              <div
                key={`${entry.action}-${index}`}
                className="rounded-lg border border-border/60 p-3"
              >
                <Text weight="medium">{entry.action}</Text>
                {entry.outcome ? (
                  <Text size="sm" className="text-muted-foreground mt-1">
                    {entry.outcome}
                  </Text>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="space-y-2">
        <Flex align="center" justify="between">
          <Heading size="xs">Linked issues</Heading>
          {relatedIssuesQuery.isFetching ? (
            <Text size="xs" className="text-muted-foreground">
              Refreshing…
            </Text>
          ) : null}
        </Flex>
        {relatedIssuesQuery.isLoading ? (
          <Flex align="center" justify="center" className="py-4">
            <LoadingOrbit size="sm" />
          </Flex>
        ) : relatedIssuesError ? (
          <Text size="sm" className="text-destructive">
            {relatedIssuesError}
          </Text>
        ) : relatedIssues.length > 0 ? (
          <div className="space-y-2">
            {relatedIssues.map((issue) => (
              <LinkedIssueItem key={issue.id} issue={issue} />
            ))}
          </div>
        ) : (
          <Text size="sm" className="text-muted-foreground">
            No linked issues yet.
          </Text>
        )}
      </section>
    </div>
  );
}

type ObservabilityContentProps = {
  canViewTrace: boolean;
  onViewTrace: () => void;
  sentryUrl: string;
  report: QaReportSummary;
};

function ObservabilityContent({
  canViewTrace,
  onViewTrace,
  sentryUrl,
  report,
}: ObservabilityContentProps) {
  return (
    <div className="space-y-4">
      <section className="space-y-2">
        <Heading size="xs">Links</Heading>
        <Flex gap="sm" wrap>
          <Button
            variant="outline"
            size="sm"
            onPress={onViewTrace}
            isDisabled={!canViewTrace}
            className="flex items-center gap-1"
          >
            View trace
            <ArrowSquareOut size={14} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onPress={() => window.open(sentryUrl, '_blank', 'noopener')}
            className="flex items-center gap-1"
          >
            View logs in Sentry
            <ArrowSquareOut size={14} />
          </Button>
        </Flex>
      </section>

      <section className="space-y-2">
        <Heading size="xs">Run identifiers</Heading>
        <div className="grid gap-3 md:grid-cols-2">
          <InfoStat label="Job ID" value={report.jobId ?? 'Not recorded'} />
          <InfoStat label="Broker job ID" value={report.brokerJobId ?? 'Not recorded'} />
          <InfoStat label="QA report ID" value={report.id} />
          <InfoStat label="Trace available" value={report.traceStorageKey ? 'Yes' : 'No'} />
        </div>
      </section>
    </div>
  );
}

function InfoStat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <Text size="sm" className="text-muted-foreground">
        {label}
      </Text>
      <Text weight="medium">{value}</Text>
    </div>
  );
}

function TaskCountPill({
  label,
  tone,
  count,
}: {
  label: string;
  tone: 'success' | 'danger' | 'muted';
  count: number;
}) {
  const variant = tone === 'success' ? 'success' : tone === 'danger' ? 'destructive' : 'outline';
  return (
    <TagBadge name={<>{label}: {count}</>} color={badgeVariantToTagColor(variant)} hideDot size="sm" />
  );
}

function getTaskStatusMeta(status: QaReportTodoStatus): {
  label: string;
  variant: BadgeVariant;
} {
  switch (status) {
    case 'healthy':
    case 'done':
      return { label: 'Passed', variant: 'success' };
    case 'unhealthy':
      return { label: 'Failed', variant: 'destructive' };
    case 'not_applicable':
      return { label: 'N/A', variant: 'outline' };
    case 'pending':
    default:
      return { label: 'Pending', variant: 'outline' };
  }
}

type LinkedIssueItemProps = {
  issue: IssueSummary;
};

function LinkedIssueItem({ issue }: LinkedIssueItemProps) {
  const primaryUrl = issue.latestOccurrence?.primaryUrl ?? null;
  // Only ever use a scheme-filtered href (http/https). primaryUrl is now
  // caller-supplied (manual/CLI filing), so a raw `href` could be a stored
  // `javascript:` sink. Fall back to plain text when it isn't a safe URL.
  const safeHref = resolveIssuePageUrl({ primaryUrl });
  const displayUrl = getIssuePageDisplayUrl({ primaryUrl });
  const issueLink = `/issues?issueId=${issue.id}`;

  return (
    <div className="rounded-lg border border-border/60 p-3 space-y-2">
      <Flex align="start" justify="between" gap="sm">
        <div className="space-y-1 min-w-0">
          <Text weight="medium" className="truncate">
            {issue.title}
          </Text>
          <Text size="sm" className="text-muted-foreground">
            {issue.category.label}
          </Text>
        </div>
        <TagBadge name={formatSeverityLabel(issue.severity)} color={badgeVariantToTagColor(issueSeverityBadgeVariants[issue.severity] ?? "default")} hideDot />
      </Flex>
      {issue.tldr ? (
        <Text size="sm" className="text-muted-foreground">
          {issue.tldr}
        </Text>
      ) : null}
      <Flex align="center" justify="between" gap="sm" wrap>
        {safeHref ? (
          <a
            href={safeHref}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-primary underline truncate max-w-[220px]"
            title={displayUrl ?? safeHref}
          >
            {displayUrl ?? safeHref}
          </a>
        ) : primaryUrl ? (
          <Text
            size="sm"
            className="text-muted-foreground truncate max-w-[220px]"
            title={primaryUrl}
          >
            {primaryUrl}
          </Text>
        ) : (
          <Text size="sm" className="text-muted-foreground">
            No primary URL
          </Text>
        )}
        <a
          href={issueLink}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1 text-sm font-medium text-primary"
        >
          View issue
          <ArrowSquareOut size={14} />
        </a>
      </Flex>
    </div>
  );
}
